import { environment } from 'src/environments/environment';
import { Api } from './api';
import { z } from 'zod';
import {
  LeaderboardPeriodType,
  LeaderboardType,
} from '../services/leaderboards/models/leaderboard-metadata';
import {
  ActivityMessageData,
  ActivityMessageDataSchema,
} from '../services/activity-feed/models/activity-message-data';
import { HttpService } from '../services/http/http.service';
import { RoomModel, RoomModelSchema } from 'src/app/model/room/room.model';
import { GameId } from 'src/app/model/game/game-id';
import { WagerTier } from 'src/app/model/wager/wager';

export const FIND_MATCH = 'find-match';
export const FINISH_ROUND = 'finish-round';
export const START_ROUND = 'start-round';
export const BOOTUP = 'bootup';
export const TIERS = 'tiers';
export const ROOMS = 'rooms';
export const LEADERBOARDS = 'game-leaderboards';
export const WAITING_OPPONENTS = 'waiting-opponents';
export const ACTIVITY_FEED = 'activity-feed';
export const PRACTICE_ROUNDS = 'practice-rounds';
export const TOURNAMENT_PUBLISHED = 'tournaments/published';
export const TOURNAMENTS = 'tournaments';
export const MINI_LEADERBOARD = 'mini-leaderboard';
export const LEADERBOARD = 'leaderboard';
export const LEADERBOARD_ENTRY = 'leaderboard/entry';
export const GAME_STATS = 'game-stats';
export const USERS = 'users';
export const INVENTORY = 'inventory';
export const SPIN_DAILY_SPINNER = 'spin-daily-spinner';
export const SPIN_ROUND_SPINNER = 'spin-round-spinner';

/* 
    Note: all functions can throw a ApiResponseError (thrown from Api in 'private/api')
    if there is response error. Client should consume this, for appropriate
    error handling. 
*/
export class WagerApi {
  private api: Api;

  private constructor(httpService: HttpService) {
    this.api = new Api(environment.wagerApi, httpService);
  }

  static request(httpService: HttpService) {
    return new WagerApi(httpService);
  }

  async bootup(): Promise<ApiResponseBootup> {
    const response = await this.api.get<ApiResponseBootup>(BOOTUP);
    if (!WagerApi.isApiResponseBootup(response)) {
      console.warn(`${BOOTUP} api response has changed`);
    }
    return response;
  }

  async randomPractice(gameId: GameId): Promise<ApiResponseRandomPractice> {
    const response = await this.api.get<ApiResponseRandomPractice>(
      `${PRACTICE_ROUNDS}/${gameId}/random`,
    );
    if (!WagerApi.isApiResponseRandomPractice(response)) {
      console.warn(`${PRACTICE_ROUNDS} api response has changed`);
    }
    return response;
  }

  async rooms(roomId: string): Promise<ApiResponseRoom> {
    const response = await this.api.get<ApiResponseRoom>(`${ROOMS}/${roomId}`);
    if (!WagerApi.isApiResponseRoom(response)) {
      console.warn(`${ROOMS} api response has changed`);
    }
    return response;
  }

  async roomTournaments(roomId: string): Promise<ApiResponseRoomTournaments> {
    const response = await this.api.get<ApiResponseRoomTournaments>(
      `${ROOMS}/${roomId}/${TOURNAMENTS}`,
    );
    if (!WagerApi.isApiResponseRoomTournaments(response)) {
      console.warn(`${ROOMS} api response has changed`);
    }
    return response;
  }

  async findMatch(
    gameId: GameId,
    tierId: string,
    depositId: string,
    tournamentId?: string | null,
  ): Promise<ApiResponseFindMatch> {
    const requestBody: ApiReqBodyFindMatch = { gameId, tierId, depositId };
    if (tournamentId && tournamentId !== null) requestBody.tournamentId = tournamentId;
    const response = await this.api.post<ApiResponseFindMatch, ApiReqBodyFindMatch>(
      FIND_MATCH,
      requestBody,
    );
    if (!WagerApi.isApiResponseFindMatch(response)) {
      console.warn(`${FIND_MATCH} api response has changed`);
    }
    return response;
  }

  async findOperatorMatch(
    gameId: GameId,
    tierId: string,
    idempotencyKey: string,
  ): Promise<ApiResponseFindMatch> {
    const response = await this.api.post<
      ApiResponseFindMatch,
      { gameId: string; tierId: string; idempotencyKey: string }
    >(FIND_MATCH, { gameId, tierId, idempotencyKey });
    if (!WagerApi.isApiResponseFindMatch(response)) {
      console.warn(`${FIND_MATCH} api response has changed`);
    }
    return response;
  }

  async startRound(roomId: string): Promise<ApiResponseStartRound> {
    const response = await this.api.post<ApiResponseStartRound, { roomId: string }>(START_ROUND, {
      roomId,
    });

    if (!WagerApi.isApiResponseStartRound(response)) {
      console.warn(`${START_ROUND} api response has changed`);
    }

    return response;
  }

  async finishRound(roomId: string, moves: string[]): Promise<ApiResponseFinishRound> {
    if (moves.length === 1 && moves[0] === '') {
      moves = new Array<string>();
    }

    const response = await this.api.post<
      ApiResponseFinishRound,
      { roomId: string; moves: string[] }
    >(FINISH_ROUND, { roomId, moves });

    if (!WagerApi.isApiResponseFinishRound(response)) {
      console.warn(`${FINISH_ROUND} api response has changed`);
    }

    return response;
  }

  async tiers() {
    const response = await this.api.get<ApiResponseTiers>(TIERS);
    if (!WagerApi.isApiResponseTiers(response)) {
      console.warn(`${TIERS} api response has changed`);
    }
    return response;
  }

  async waitingOpponents(
    gameId: GameId,
    duelBucketId = 'default',
  ): Promise<ApiResponseWaitingOpponents> {
    const response = await this.api.get<ApiResponseWaitingOpponents>(
      `${WAITING_OPPONENTS}/${gameId}/${duelBucketId}`,
    );
    if (!WagerApi.isApiResponseWaitingOpponents(response)) {
      console.warn(`${WAITING_OPPONENTS} api response has changed`);
    }
    return response;
  }

  async leaderboardList(
    tournamentId: string,
    page: number,
  ): Promise<ApiResponseTournamentLeaderboardList> {
    const response = await this.api.get<ApiResponseTournamentLeaderboardList>(
      `${TOURNAMENTS}/${tournamentId}/${LEADERBOARD}/${page}`,
    );
    if (!WagerApi.isApiResponseLeaderboardList(response)) {
      console.warn(`${TOURNAMENTS} api response has changed`);
    }
    return response;
  }

  async leaderboardEntry(tournamentId: string): Promise<ApiResponseTournamentLeaderboardEntry> {
    const response = await this.api.get<ApiResponseTournamentLeaderboardEntry>(
      `${TOURNAMENTS}/${tournamentId}/${LEADERBOARD_ENTRY}`,
    );
    if (!WagerApi.isApiResponseLeaderboardEntry(response)) {
      console.warn(`${TOURNAMENTS} api response has changed`);
    }
    return response;
  }

  async leaderboards(
    gameId: GameId,
    leaderboardType: LeaderboardType,
    timePeriod: LeaderboardPeriodType,
  ): Promise<ApiResponseLeaderboards> {
    const response = await this.api.get<ApiResponseLeaderboards>(
      `${LEADERBOARDS}/${gameId}/${leaderboardType}/${timePeriod}`,
    );
    if (!WagerApi.isApiResponseLeaderboards(response)) {
      console.warn(`${LEADERBOARDS} api response has changed`);
    }
    return response;
  }

  async stats(userId: string, gameId: string): Promise<ApiResponseStats> {
    const response = await this.api.get<ApiResponseStats>(
      `${USERS}/${userId}/${GAME_STATS}/${gameId}`,
    );
    if (!WagerApi.isApiResponseStats(response)) {
      console.warn(`${GAME_STATS} api response has changed`);
    }
    return response;
  }

  async activityFeed(gameId?: string): Promise<ApiResponseActivityFeed> {
    const url = gameId ? `${ACTIVITY_FEED}?gameId=${gameId}` : ACTIVITY_FEED;
    const response = await this.api.get<ApiResponseActivityFeed>(url);
    if (!WagerApi.isApiResponseActivityFeed(response)) {
      console.warn(`${ACTIVITY_FEED} api response has changed`);
    }
    return response;
  }

  async publishedTournaments(): Promise<ApiResponsePublishedTournaments> {
    const response = await this.api.get<ApiResponsePublishedTournaments>(TOURNAMENT_PUBLISHED);
    if (!WagerApi.isApiResponsePublisedTournaments(response)) {
      console.warn(`${TOURNAMENT_PUBLISHED} api response has changed`);
    }
    return response;
  }

  async tournamentData(tournamentId: string): Promise<ApiResponseTournamentData> {
    const response = await this.api.get<ApiResponseTournamentData>(
      `${TOURNAMENTS}/${tournamentId}`,
    );
    if (!WagerApi.isApiResponseTournamentData(response)) {
      console.warn(`${TOURNAMENTS} api response has changed`);
    }
    return response;
  }

  async miniLeaderboardData(tournamentId: string): Promise<ApiResponseMiniLeaderboardData> {
    const response = await this.api.get<ApiResponseMiniLeaderboardData>(
      `${TOURNAMENTS}/${tournamentId}/${MINI_LEADERBOARD}`,
    );
    if (!WagerApi.isApiResponseMiniLeaderboardData(response)) {
      console.warn(`${TOURNAMENTS} api response has changed`);
    }
    return response;
  }

  async inventory(): Promise<ApiResponseInventory> {
    const response = await this.api.get<ApiResponseInventory>(INVENTORY);
    if (!WagerApi.isApiResponseInventory(response)) {
      console.warn(`${INVENTORY} api response has changed`);
    }
    return response;
  }

  async dailySpinner(nextSpinAvailableAt: number): Promise<ApiResponseSpinDailySpinner> {
    const response = await this.api.post<
      ApiResponseSpinDailySpinner,
      { nextSpinAvailableAt: number }
    >(SPIN_DAILY_SPINNER, {
      nextSpinAvailableAt,
    });

    if (!WagerApi.isApiResponseSpinDailySpinner(response)) {
      console.warn(`${SPIN_DAILY_SPINNER} api response has changed`);
    }

    return response;
  }

  async spinRoundSpinner(): Promise<ApiResponseSpinRoundSpinner> {
    const response = await this.api.post<ApiResponseSpinRoundSpinner, null>(SPIN_ROUND_SPINNER);

    if (!WagerApi.isApiResponseSpinRoundSpinner(response)) {
      console.warn(`${SPIN_ROUND_SPINNER} api response has changed`);
    }

    return response;
  }

  static isApiResponseTiers(obj: unknown): obj is ApiResponseTiers {
    const dataSchema = z.object({
      tierId: z.string(),
      currency: z.string(),
      entryFee: z.number(),
      reward: z.number(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: z.array(dataSchema),
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseRandomPractice(obj: unknown): obj is ApiResponseRandomPractice {
    const dataSchema = z.object({
      opponentUserId: z.string().nullable().optional(),
      opponentName: z.string(),
      opponentScore: z.number(),
      secrets: z.array(
        z.object({
          index: z.number(),
          value: z.number(),
        }),
      ),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseFindMatch(obj: unknown): obj is ApiResponseFindMatch {
    const dataSchema = z.object({
      roomId: z.string(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseRoom(obj: unknown): obj is ApiResponseRoom {
    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: RoomModelSchema,
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseRoomTournaments(obj: unknown): obj is ApiResponseRoom {
    const PrizeSchema = z.object({
      count: z.number(),
      amount: z.number(),
    });

    const TournamentSchema = z.object({
      tournamentId: z.string(),
      scoreFormula: z.literal('SUM_OF_WON_CENTS'),
      title: z.string(),
      bannerUrl: z.string(),
      startDate: z.number(),
      endDate: z.number(),
      joinUntilDate: z.number(),
      roomDeadline: z.number(),
      finished: z.boolean(),
      gameIds: z.array(z.nativeEnum(GameId)),
      leaderboard: z.object({
        prizes: z.array(PrizeSchema),
      }),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: TournamentSchema,
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseBootup(obj: unknown): obj is ApiResponseBootup {
    const featureFlagsSchema = z.object({
      enableReferrals: z.boolean(),
      enableLeaderboards: z.boolean(),
      enableActivityFeed: z.boolean(),
      enableEmailLink: z.boolean(),
    });

    const schema = z.object({
      appleReviewVersion: z.string().optional(),
      minWebVersion: z.number(),
      minAndroidVersion: z.number(),
      minIOSVersion: z.number(),
      androidDownloadLink: z.string(),
      iosDownloadLink: z.string(),
      featureFlags: featureFlagsSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseStartRound(obj: unknown): obj is ApiResponseStartRound {
    const dataSchema = z.object({
      dealId: z.string(),
      dealerUrl: z.string(),
      deadline: z.number(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseFinishRound(obj: unknown): obj is ApiResponseFinishRound {
    const schema = z.object({
      status: z.number(),
      msg: z.string(),
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseGeoCode(obj: unknown): obj is ApiResponseGeoCode {
    const dataSchema = z.object({
      countryCode: z.string(),
      state: z.string().optional(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseWaitingOpponents(obj: unknown): obj is ApiResponseWaitingOpponents {
    const dataSchema = z.object({
      waitingOpponents: z.record(z.boolean()),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseLeaderboards(obj: unknown): obj is ApiResponseLeaderboards {
    const leaderboardEntrySchema = z.object({
      nick: z.string(),
      position: z.number(),
      rank: z.number(),
      value: z.number(),
    });
    const dataSchema = z.object({
      lastUpdated: z.number(),
      leaderboardData: z.object({}).catchall(leaderboardEntrySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseLeaderboardList(obj: unknown): obj is ApiResponseTournamentLeaderboardList {
    const LeaderBoardEntrySchema = z.object({
      nick: z.string(),
      userId: z.string(),
      score: z.number(),
    });
    const dataSchema = z.object({
      page: z.number(),
      entriesPerPage: z.number(),
      hasMoreEntries: z.boolean(),
      entries: z.array(LeaderBoardEntrySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseLeaderboardEntry(obj: unknown): obj is ApiResponseTournamentLeaderboardEntry {
    const dataSchema = z.object({
      score: z.number(),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseActivityFeed(obj: unknown): obj is ApiResponseLeaderboards {
    const dataSchema = z.object({
      serverTime: z.number(),
      activities: z.record(z.string(), z.record(z.string(), ActivityMessageDataSchema)),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponsePublisedTournaments(obj: unknown): obj is ApiResponsePublishedTournaments {
    const PrizeSchema = z.object({
      count: z.number(),
      amount: z.number(),
    });

    const TournamentSchema = z.object({
      tournamentId: z.string(),
      scoreFormula: z.literal('SUM_OF_WON_CENTS'),
      title: z.string(),
      bannerUrl: z.string(),
      startDate: z.number(),
      endDate: z.number(),
      joinUntilDate: z.number(),
      roundDeadline: z.number(),
      finished: z.boolean(),
      gameIds: z.array(z.nativeEnum(GameId)),
      leaderboard: z.object({
        prizes: z.array(PrizeSchema),
      }),
    });

    const LeaderBoardEntrySchema = z.object({
      nick: z.string(),
      userId: z.string(),
      score: z.number(),
    });

    const PublishedTournamentSchema = z.object({
      tournament: TournamentSchema,
      entries: z.array(LeaderBoardEntrySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: z.array(PublishedTournamentSchema),
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseTournamentData(obj: unknown): obj is ApiResponseTournamentData {
    const PrizeSchema = z.object({
      count: z.number(),
      amount: z.number(),
    });

    const TournamentSchema = z.object({
      tournamentId: z.string(),
      scoreFormula: z.literal('SUM_OF_WON_CENTS'),
      title: z.string(),
      bannerUrl: z.string(),
      startDate: z.number(),
      endDate: z.number(),
      joinUntilDate: z.number(),
      roomDeadline: z.number(),
      finished: z.boolean(),
      gameIds: z.array(z.nativeEnum(GameId)),
      leaderboard: z.object({
        prizes: z.array(PrizeSchema),
      }),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: TournamentSchema.nullable(),
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseMiniLeaderboardData(obj: unknown): obj is ApiResponseMiniLeaderboardData {
    const PrizeSchema = z.object({
      count: z.number(),
      amount: z.number(),
    });

    const TournamentSchema = z.object({
      tournamentId: z.string(),
      scoreFormula: z.literal('SUM_OF_WON_CENTS'),
      title: z.string(),
      bannerUrl: z.string(),
      startDate: z.number(),
      endDate: z.number(),
      joinUntilDate: z.number(),
      roomDeadline: z.number(),
      finished: z.boolean(),
      gameIds: z.array(z.nativeEnum(GameId)),
      leaderboard: z.object({
        prizes: z.array(PrizeSchema),
      }),
    });

    const LeaderBoardEntrySchema = z.object({
      nick: z.string(),
      userId: z.string(),
      score: z.number(),
    });

    const PublishedTournamentSchema = z.object({
      tournament: TournamentSchema,
      entries: z.array(LeaderBoardEntrySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: PublishedTournamentSchema,
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseStats(obj: unknown): obj is ApiResponseStats {
    const StatsEntrySchema = z.object({
      value: z.number(),
      top: z.number(),
    });

    const dataSchema = z.object({
      nick: z.string(),
      globalRank: z.number(),
      activeDays: z.number(),

      bestScore: StatsEntrySchema,
      averageScore: StatsEntrySchema,
      winRate: StatsEntrySchema,
      completedRounds: StatsEntrySchema,
      wonAmount: StatsEntrySchema,
      wonRounds: StatsEntrySchema,
      drawnRounds: StatsEntrySchema,
      bestWinStreak: StatsEntrySchema,
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseInventory(obj: unknown): obj is ApiResponseInventory {
    const SpinnerPrizeBountySchema = z.object({
      type: z.literal('BOUNTY'),
      tierId: z.string(),
      amount: z.number(),
      jackpot: z.boolean(),
    });

    const DailySpinnerSchema = z
      .object({
        prizes: z.array(SpinnerPrizeBountySchema),
        nextSpinAvailableAt: z.number(),
      })
      .nullable();

    const RoundSpinnerSchema = z
      .object({
        prizes: z.array(SpinnerPrizeBountySchema),
        progress: z.number(),
        target: z.number(),
      })
      .nullable();

    const BountySchema = z
      .object({
        tierId: z.string(),
        amount: z.number(),
      })
      .nullable();

    const InventoryViewSchema = z.object({
      dailySpinner: DailySpinnerSchema,
      roundSpinner: RoundSpinnerSchema,
      bounty: BountySchema,
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: InventoryViewSchema,
    });

    return schema.safeParse(obj).success;
  }

  static isApiResponseSpinDailySpinner(obj: unknown): obj is ApiResponseSpinDailySpinner {
    const SpinnerPrizeBountySchema = z.object({
      type: z.literal('BOUNTY'),
      tierId: z.string(),
      amount: z.number(),
      jackpot: z.boolean(),
    });

    const dataSchema = z.object({
      nextSpinAvailableAt: z.number(),
      winningPrizeIndex: z.number().nullable(),
      prizes: z.array(SpinnerPrizeBountySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }

  static isApiResponseSpinRoundSpinner(obj: unknown): obj is ApiResponseSpinRoundSpinner {
    const SpinnerPrizeBountySchema = z.object({
      type: z.literal('BOUNTY'),
      tierId: z.string(),
      amount: z.number(),
      jackpot: z.boolean(),
    });

    const dataSchema = z.object({
      winningPrizeIndex: z.number().nullable(),
      prizes: z.array(SpinnerPrizeBountySchema),
    });

    const schema = z.object({
      status: z.number(),
      msg: z.string(),
      data: dataSchema,
    });
    return schema.safeParse(obj).success;
  }
}

export interface ApiResponseGeoCode {
  status: number;
  msg: string;
  data: {
    countryCode: string;
    state: string;
  };
}

export interface ApiResponseFindMatch {
  status: number;
  msg: string;
  data: {
    roomId: string;
  };
}

export interface StakeTier {
  stake: number;
  id: string;
}

export interface ApiResponseBootup {
  appleReviewVersion?: string;
  minWebVersion: number;
  minAndroidVersion: number;
  minIOSVersion: number;
  androidDownloadLink: string;
  iosDownloadLink: string;
  minOperatorVersion: number;

  // TODO: Make these non-optional once the backend is updated
  minInHouseWidgetVersion?: number;
  minOperatorWidgetVersion?: number;

  featureFlags: {
    enableReferrals: boolean;
    enableLeaderboards: boolean;
    enableActivityFeed: boolean;
    enableEmailLink: boolean;
  };
}

export interface ApiResponseRoom {
  status: number;
  msg: string;
  data: RoomModel;
}

export interface ApiResponseRoomTournaments {
  status: number;
  msg: string;
  data: TournamentView[];
}

export interface ApiResponseStartRound {
  status: number;
  msg: string;
  data: {
    dealId: string;
    dealerUrl: string;
    deadline: number;
  };
}

export interface ApiResponseFinishRound {
  status: number;
  msg: string;
}

export interface ApiResponseTiers {
  status: number;
  msg: string;
  data: WagerTier[];
}

export interface ApiResponseRandomPractice {
  status: number;
  msg: string;
  data: {
    opponentUserId: string | undefined;
    opponentName: string;
    opponentScore: number;
    secrets: [
      {
        index: number;
        value: number;
      },
    ];
  };
}

export interface WaitingOpponents {
  waitingOpponents: {
    [key: string]: boolean;
  };
}

export interface ApiResponseWaitingOpponents {
  status: number;
  msg: string;
  data: WaitingOpponents;
}

export interface LeaderboardData {
  [userId: string]: {
    value: number;
    nick: string;
    position: number;
    rank: number;
    operatorId: string;
  };
}

export interface Leaderboards {
  lastUpdated: number;
  leaderboardData: LeaderboardData;
}

export interface ApiResponseLeaderboards {
  status: number;
  msg: string;
  data: Leaderboards;
}

export interface ActivityFeed {
  serverTime: number;
  activities: {
    [time: string]: {
      [activityId: string]: ActivityMessageData;
    };
  };
}

export interface ApiResponseActivityFeed {
  status: number;
  msg: string;
  data: ActivityFeed;
}

export interface TournamentView {
  tournamentId: string;
  scoreFormula: 'SUM_OF_WON_CENTS';
  title: string;
  bannerUrl: string;
  startDate: number;
  endDate: number;
  joinUntilDate: number;
  roundDeadline: number;
  finished: boolean;
  gameIds: GameId[];
  leaderboard: {
    prizes: {
      count: number;
      amount: number;
    }[];
  };
}

export interface LeaderBoardEntry {
  nick: string;
  userId: string;
  score: number;
}

export interface PublishedTournament {
  tournament: TournamentView;
  entries: LeaderBoardEntry[];
}

export interface MiniLeaderboardTournament {
  tournament: TournamentView;
  entries: LeaderBoardEntry[];
}

export interface ApiResponsePublishedTournaments {
  status: number;
  msg: string;
  data: PublishedTournament[];
}

export interface ApiResponseTournamentData {
  status: number;
  msg: string;
  data: TournamentView | null;
}

export interface ApiResponseMiniLeaderboardData {
  status: number;
  msg: string;
  data: MiniLeaderboardTournament;
}

export interface ApiResponseTournamentLeaderboardList {
  status: number;
  msg: string;
  data: {
    page: number;
    entriesPerPage: number;
    hasMoreEntries: boolean;
    entries: LeaderBoardEntry[];
  };
}

export interface ApiResponseTournamentLeaderboardEntry {
  status: number;
  msg: string;
  data: {
    score: number;
  };
}

export interface ApiReqBodyFindMatch {
  gameId: GameId;
  tierId: string;
  depositId: string;
  tournamentId?: string;
}

export interface UserStats {
  nick: string;
  globalRank: number;
  activeDays: number;

  bestScore: StatsEntry;
  averageScore: StatsEntry;
  winRate: StatsEntry;
  completedRounds: StatsEntry;
  wonAmount: StatsEntry;
  wonRounds: StatsEntry;
  drawnRounds: StatsEntry;
  bestWinStreak: StatsEntry;
}

export interface StatsEntry {
  value: number;
  top: number; // <- 0-100
}

export interface ApiResponseStats {
  status: number;
  msg: string;
  data: UserStats;
}

export interface ApiResponseInventory {
  status: number;
  msg: string;
  data: InventoryView;
}

export interface Bounty {
  tierId: string;
  amount: number;
}

export interface InventoryView {
  dailySpinner: DailySpinnerView | null;
  roundSpinner: RoundSpinnerView | null;
  bounty: Bounty | null;
}

export interface DailySpinnerView {
  prizes: SpinnerPrizeBounty[];
  nextSpinAvailableAt: number;
}

export interface RoundSpinnerView {
  prizes: SpinnerPrizeBounty[];
  progress: number;
  target: number;
}

export interface SpinnerPrizeBounty {
  type: 'BOUNTY';
  tierId: string;
  amount: number;
  jackpot: boolean;
}

export interface ApiResponseSpinDailySpinner {
  status: number;
  msg: string;
  data: DailySpinResult;
}

export interface DailySpinResult {
  nextSpinAvailableAt: number;
  winningPrizeIndex: number | null;
  prizes: SpinnerPrizeBounty[];
}

export interface ApiResponseSpinRoundSpinner {
  status: number;
  msg: string;
  data: RoundSpinResult;
}

export interface RoundSpinResult {
  winningPrizeIndex: number | null;
  prizes: SpinnerPrizeBounty[];
}
